import Vue from 'vue';
import Router from 'vue-router';
import { isCustomerAuthenticated } from '@/lib/login_utils';
import { authState } from '@/store/getters/auth';
import { updateLayoutConfig } from '@/lib/layout_config';
import { layoutConfig } from '@/lib/layout_consts';
import { get } from '@/lib/helpers';
import { layoutConfig as currentLayoutConfig } from '@/store/getters/app_config';
import { retrieveBigCookie } from '@/store/actions/customer';
import { isMicroappsEnabled } from '@/lib/utils';
import {
  redirectToStoreOrFallback,
  REPAYMENT_DEEP_LINK_PATHS,
  wantsWebFallback,
} from '@/lib/store_link_utils';
import { storeAccessDetails } from '@/store/actions/auth';
import { appRoutes } from './appRoutes';
import { cmRoutes } from './cmRoutes';
import { gpayRoutes } from './gpayRoutes';
import { qualRoutes } from './qualRoutes';
import { gpayQualRoutes } from './gpayQualRoutes';
import { magicLinkRoutes } from './magicLinkRoutes';
import { loanRestructureRoutes } from './loanRestructureRoutes';

Vue.use(Router);

// Explicit allowlist of the 3 original deep-link paths — desktop/unknown UA
// falls back to the in-app aipHome route (same origin as app.freo.money, so
// an already-authenticated visitor stays logged in), unlike NotFound.vue's
// broader /app/*+/one_app/* catch-all below, which currently falls back to
// VUE_APP_PUBLIC_BASE_URL (a different origin, loses any session).
const redirectToStore = (_to: any, _from: any, next: any) => {
  redirectToStoreOrFallback(() =>
    next({
      name: get(layoutConfig, 'DEFAULT_LAYOUT.routes.aipHome', 'aipHome'),
    })
  );
};

const DEEP_LINK_ROUTES = [
  '/app/one_app/borrow/moneytap/emandate',
  '/app/one_app/borrow/moneytap/bd',
  '/app/one_app/borrow/moneytap/kyc',
].map((path) => ({
  path,
  meta: { requiresAuth: false },
  beforeEnter: redirectToStore,
  component: () => import('@/views/NotFound.vue'),
}));

// The two collections repayment links, marked with ?target=web (see
// wantsWebFallback). The path is shared with the plain, unmarked link, so the
// decision cannot be made by route matching alone and is made here, in a guard,
// before any component resolves.
//
// Registering the base paths at all is new. They used to have no route and fell
// through to the '*' catch-all, which is what sent them to the store. That
// destination is preserved below for a link arriving without the param, so a
// link already in a customer's inbox behaves exactly as it does today.
//
// beforeEnter runs before the route's component is fetched, and every path out
// of it either redirects internally or leaves the page, so the not-found chunk
// is never loaded and the 404 screen never renders. The component below exists
// only because a route needs one; it is deliberately unreachable.
const REPAYMENT_DEEP_LINK_ROUTES = REPAYMENT_DEEP_LINK_PATHS.map((path) => ({
  path,
  meta: { requiresAuth: false },
  beforeEnter: (to: any, _from: any, next: any) => {
    if (wantsWebFallback(to.query)) {
      next({
        name: get(
          layoutConfig,
          'CREDIT_MANAGEMENT_LAYOUT.routes.repayments',
          'cm-repayments'
        ),
      });
      return;
    }
    redirectToStore(to, _from, next);
  },
  component: () => import('@/views/NotFound.vue'),
}));

const router = new Router({
  mode: 'history',
  base: process.env.VUE_APP_PUBLIC_PATH,
  routes: [
    ...DEEP_LINK_ROUTES,
    ...REPAYMENT_DEEP_LINK_ROUTES,
    appRoutes,
    cmRoutes,
    gpayRoutes,
    {
      path: '/',
      name: get(layoutConfig, 'DEFAULT_LAYOUT.routes.login', ''),
      component: () =>
        import(/* webpackChunkName: "login" */ '@/views/CustomerLogin.vue'),
      beforeEnter: async (to, from, next) => {
        if (isMicroappsEnabled()) {
          updateLayoutConfig('GPAY_LAYOUT');
          next({ path: '/gpay' });
        } else {
          updateLayoutConfig('DEFAULT_LAYOUT');
          next();
        }
      },
    },
    {
      path: '/login',
      name: get(layoutConfig, 'DEFAULT_LAYOUT.routes.loginComponent', ''),
      component: () =>
        import(
          /* webpackChunkName: "loginComponent" */ '@/components/LoginComponent.vue'
        ),
      beforeEnter: async (to, from, next) => {
        if (isMicroappsEnabled()) {
          updateLayoutConfig('GPAY_LAYOUT');
          next({ path: '/gpay' });
        } else {
          updateLayoutConfig('DEFAULT_LAYOUT');
          next();
        }
      },
    },
    // Login-via-code route — user lands here from SMS link (/login-code/:code);
    // requiresAuth: false so the auth guard does not redirect before the code is exchanged
    {
      path: '/login-code/:code',
      name: 'loginCode',
      meta: { requiresAuth: false },
      component: () =>
        import(
          /* webpackChunkName: "loginCode" */ '@/components/LoginCode.vue'
        ),
      beforeEnter: async (to, from, next) => {
        updateLayoutConfig('DEFAULT_LAYOUT');
        next();
      },
    },
    {
      path: '/gpay',
      name: get(layoutConfig, 'GPAY_LAYOUT.routes.login', ''),
      component: () =>
        import(
          /* webpackChunkName: "gpay-wrapper" */ '@/views/GpayWrapper.vue'
        ),
    },
    {
      path: '/post-aip-reject',
      name: 'bankerPortalRejected',
      meta: { requiresAuth: true },
      redirect: {
        name: get(
          layoutConfig,
          'DEFAULT_LAYOUT.routes.rejectedApplication',
          ''
        ),
      },
      component: () =>
        import(
          /* webpackChunkName: "post-aip-reject" */ '@/views/PostAipReject.vue'
        ),
    },
    qualRoutes,
    gpayQualRoutes,
    magicLinkRoutes,
    loanRestructureRoutes,
    {
      path: '/error',
      name: 'genericError',
      props: true,
      component: () => import('@/views/CustomError.vue'),
    },
    {
      // Moved before the '*' catch-all below — a route registered after a
      // wildcard is unreachable in Vue Router 3 (array-order matching), which
      // silently broke this page (external redirect target, reads
      // $route.query.message — see SuccessOperationPage.vue).
      path: '/success-page',
      name: 'OperationSuccess',
      component: () =>
        import(
          /* webpackChunkName: "not-found" */ '@/views/SuccessOperationPage.vue'
        ),
    },
    {
      path: '*',
      name: '404 Not Found',
      component: () =>
        import(/* webpackChunkName: "not-found" */ '@/views/NotFound.vue'),
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  let requiresAuth = true;
  requiresAuth = to.matched.some((record: any) => record.meta.requiresAuth);
  const queryObj: any = to.query || {};
  const isCtxAuthenticated = await isCustomerAuthenticated(authState());
  console.log(to.hash);

  console.log(
    'Router - beforeEach : ',
    to.name,
    ' | requiresAuth: ',
    requiresAuth,
    ' | isCtxAuthenticated: ',
    isCtxAuthenticated
  );
  if (to.hash.length > 0) {
    next(to.hash.split('#')[1]);
  }
  if (queryObj.ml) {
    next({
      name: 'home-ml',
      params: { linkId: queryObj.ml },
    });
  }
  if (queryObj.token) {
    storeAccessDetails({
      payload: { access_token: queryObj.token, authenticated: true },
    });
    try {
      await retrieveBigCookie(true, 'router:beforeEach');
      next();
    } catch (error: any) {
      console.log(error);
      if (!(error && error.status)) {
        const newQueryParams: any = { redirect: to.name };
        Object.keys(to.query).map((val) => {
          if (val === 'token') return;
          newQueryParams[val] = to.query[val];
        });
        next({
          name: get(currentLayoutConfig(), 'routes.login', 'customerLogin'),
          query: newQueryParams,
        });
      } else {
        const newQueryParams: any = { redirect: to.name };
        Object.keys(to.query).map((val) => {
          if (val === 'token') return;
          newQueryParams[val] = to.query[val];
        });
        next({
          name: get(currentLayoutConfig(), 'routes.login', 'customerLogin'),
          query: { ...newQueryParams },
        });
      }
    }
  } else if (
    requiresAuth &&
    !isCtxAuthenticated &&
    // Excludes loginCode so unauthenticated users are not redirected before the code-to-token exchange completes
    !['customerLogin', 'gpay-customerLogin', 'loginCode'].includes(
      to.name as string
    )
  ) {
    next({
      name: get(currentLayoutConfig(), 'routes.login', 'customerLogin'),
      query: { redirect: to.name, ...to.query },
    });
  } else {
    next();
  }
});

export default router;
